<script>
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import {useCampaignStore} from "@/javascript/stores/campaign";
import VueDatePicker from "@vuepic/vue-datepicker";
import CurrencyInput from "../../components/currencyInput/currencyInput.vue";
import DragNDrop from "../brand/dragNDrop.vue";
import BaseModal from "@/javascript/views/components/BaseModal.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import Tooltip from "../../components/tooltip.vue";

export default {
    components: {
      Tooltip,
      DragNDrop,
      CurrencyInput,
      VueDatePicker,
      LoadingSpinner,
      BaseModal,
    },
    setup() {
      const campaignStore = useCampaignStore();
      const analyticEventStore = useAnalyticEventStore();
      return {
        campaignStore,
        analyticEventStore,
      };
    },
    data() {
      return {
        errors: {},
        saving: false,
        saved: false,
        newCampaign: {
          name: "",
          fund_id: null,
          start_date: null,
          end_date: null,
          donation_target: 0,
          banner_image: null,
          short_description: "",
          description: "",
          donor_wall_enabled: true,
          visible: true,
          donation_suggestions_enabled: true,
          donation_suggestions: [25, 50, 100, 250]
        },
        addingSuggestion: false,
        tempSuggestion: ""
      };
    },
    watch: {},
    created() {
      window.addEventListener("resize", this.onWindowResize);
    },
    unmounted() {
      window.removeEventListener("resize", this.onWindowResize);
    },
    mounted() {
      this.campaignStore.getFunds();
    },
    methods: {
      validate() {
        this.errors = {};
        if (!this.newCampaign.name) {
          this.errors['name'] = "Name required";
        }
        if (!this.newCampaign.fund_id) {
          this.errors['fund'] = "Fund Required";
        }
        if (!this.newCampaign.start_date) {
          this.errors['start_date'] = "Required";
        }
        if (this.newCampaign.start_date && this.newCampaign.end_date &&
            new Date(this.newCampaign.start_date) >= new Date(this.newCampaign.end_date)) {
          this.errors['end_date'] = "End date must be after start date";
        }
        return Object.entries(this.errors).length === 0;
      },
      async save() {
        if (this.validate()) {
          this.saving = true;
          this.campaignStore.createCampaign(this.newCampaign).then((_) => {
            this.saved = true;
            this.analyticEventStore.logEvent({
              action: `new-campaign-modal-save`,
              metadata: {
                campaign: this.newCampaign
              }
            });
          });
        }
      },
      handleFileSelected(file) {
        this.newCampaign.banner_image = file;
      },
      addSuggestion() {
        const amount = parseFloat(this.tempSuggestion);
        // Make sure duplicates cannot be added | Only 4 suggestions allowed also
        if (!isNaN(amount) && amount > 0 && this.newCampaign.donation_suggestions.length < 4 && !this.newCampaign.donation_suggestions.includes(amount)) {
          this.newCampaign.donation_suggestions.push(amount);
        }
        this.tempSuggestion = "";
        this.addingSuggestion = false;
      },
      closeModal() {
        this.campaignStore.showNewModal = false;
        this.analyticEventStore.logEvent({
          action: `new-campaign-modal-close`,
        });
      },
      /**
       * On window resize the datepicker calendar needs to be closed because
       * with how the positioning on vue-datepicker works it get wonky
       * when the window is resized.
       */
      onWindowResize() {
        this.$refs["start-datepicker"].closeMenu();
        this.$refs["end-datepicker"].closeMenu();
      }
    }
  };
</script>

<template>
  <BaseModal
    title="Create a New Campaign"
    size="medium"
    @close="closeModal"
  >
    <template #default>
      <!-- Show success message -->
      <div
        v-if="saved"
        class="m-20 text-blue-500 text-large text-center"
      >
        Campaign Created!
      </div>
      <!-- Show loading spinner -->
      <div
        v-else-if="saving"
        class="m-20"
      >
        <LoadingSpinner size="small" />
      </div>
      <!-- Main form -->
      <div
        v-else
        class="overflow-y-auto flex-col h-full"
      >
        <div class="border-t border-gray-300 my-2" />
        <div class="flex flex-col justify-center">
          <div class="transition-all">
            <div>
              <div>
                <label
                  for="campaign_name"
                  class="text-xs font-semibold mt-2"
                >
                  Campaign Name*
                </label>
                <input
                  id="campaign_name"
                  v-model="newCampaign.name"
                  placeholder="Enter Name"
                  class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                <span class="text-xs text-red-700">
                  {{ errors['name'] }}
                </span>
              </div>

              <!--   New Short Description Column -->
              <div>
                <label
                  for="short_description"
                  class="text-xs font-semibold mt-2"
                >
                  Short Description
                </label>
                <tooltip
                  class="mt-3 ml-2"
                  message="A brief description of your giving need."
                  color="#3a4473"
                />
                <textarea
                  id="short_description"
                  v-model="newCampaign.short_description"
                  rows="1"
                  cols="20"
                  placeholder="Enter a short description ..."
                  class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                />
                <!--                If we want to add a character count     -->
                <p
                  class="text-right text-xs text-gray-500"
                >
                  {{ newCampaign.short_description.length }}/100
                </p>
              </div>

              <!--  Kept the original description column - only call it Long Description for labels-->
              <div>
                <label
                  for="description"
                  class="text-xs font-semibold mt-2"
                >
                  Long Description
                </label>
                <tooltip
                  class="mt-3 ml-2"
                  message="A longer description for donors looking for more information on your current needs."
                  color="#3a4473"
                />
                <textarea
                  id="description"
                  v-model="newCampaign.description"
                  rows="3"
                  cols="20"
                  placeholder="Enter a long description ..."
                  class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                />
              </div>

              <!-- Select Fund -->
              <div>
                <label
                  for="fund"
                  class="text-xs font-semibold mt-2"
                >
                  Fund*
                </label>
                <select
                  id="fund"
                  v-model="newCampaign.fund_id"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option
                    value=""
                    disabled
                    selected
                  >
                    Select a fund
                  </option>
                  <option
                    v-for="fund in campaignStore.funds"
                    :key="fund.id"
                    :value="fund.id"
                  >
                    {{ fund.name }}
                  </option>
                </select>

                <span class="text-xs text-red-700">
                  {{ errors['fund'] }}
                </span>
              </div>

              <!-- Date Picker -->
              <div class="flex flex-row">
                <div class="mr-2">
                  <label
                    for="start_date"
                    class="text-xs font-semibold"
                  >
                    Start Date*
                  </label>
                  <vue-date-picker
                    id="start_date"
                    ref="start-datepicker"
                    v-model="newCampaign.start_date"
                    :enable-time-picker="false"
                    placeholder="Select"
                    prevent-min-max-navigation
                    auto-apply
                    teleport
                    class="max-w-[300px]"
                  />
                  <span class="text-xs text-red-700">
                    {{ errors['start_date'] }}
                  </span>
                </div>
                <div>
                  <label
                    for="end_date"
                    class="text-xs font-semibold mt-2"
                  >
                    End Date
                  </label>
                  <vue-date-picker
                    id="end_date"
                    ref="end-datepicker"
                    v-model="newCampaign.end_date"
                    :enable-time-picker="false"
                    placeholder="Select"
                    prevent-min-max-navigation
                    auto-apply
                    teleport
                    class="max-w-[300px]"
                  />
                  <span class="text-xs text-red-700">
                    {{ errors['end_date'] }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Target Dollars Input -->
            <div>
              <label
                for="target"
                class="text-xs font-semibold mt-2"
              >
                Target Dollars
              </label>
              <currency-input
                id="target"
                v-model="newCampaign.donation_target"
                prefix="$ "
                :max="20000000"
                class="mt-1 mb-2 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
            </div>

            <!-- Donation Suggestions -->
            <div class="flex items-center gap-2 mt-4">
              <input
                id="donation_suggestions_enabled"
                v-model="newCampaign.donation_suggestions_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
              >
              <label for="donation_suggestions_enabled" class="text-xs font-semibold cursor-pointer">
                Donation Suggestions
              </label>
              <tooltip
                message="Show suggested donation amounts to donors on the giving form."
                color="#3a4473"
              />
            </div>
            <div v-if="newCampaign.donation_suggestions_enabled" class="mt-2">
              <div class="flex flex-wrap items-center gap-2">
                <div
                  v-for="(amount, idx) in newCampaign.donation_suggestions"
                  :key="idx"
                  class="flex items-center gap-1 bg-gray-100 border border-gray-300 rounded-full px-3 h-8"
                >
                  <span class="text-sm font-semibold">${{ amount }}</span>
                  <button
                    class="text-gray-400 hover:text-red-500 ml-1 font-bold leading-none"
                    @click="newCampaign.donation_suggestions.splice(idx, 1)"
                  >×</button>
                </div>
                <div v-if="addingSuggestion" class="flex items-center gap-1">
                  <input
                    v-model="tempSuggestion"
                    type="number"
                    class="w-20 border border-gray-300 rounded px-2 py-1 text-sm h-8"
                    placeholder="$"
                    @keyup.enter="addSuggestion"
                  >
                  <button
                    class="bg-default_secondary_dark text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                    @click="addSuggestion"
                  >✓</button>
                </div>
                <button
                  v-else-if="newCampaign.donation_suggestions.length < 4"
                  class="bg-default_secondary_dark text-white rounded-full w-8 h-8 flex items-center justify-center text-sm"
                  @click="addingSuggestion = true"
                >+</button>
              </div>
              <p class="text-xs text-gray-500 mt-1">Maximum of 4 suggestions.</p>
            </div>

            <!-- Banner Image -->
            <div>
              <label
                for="banner"
                class="text-xs font-semibold mt-2"
              >
                Banner Image
              </label>
              <drag-n-drop
                id="banner"
                compact
                validate
                file-type="image"
                @file-selected="handleFileSelected"
              />
            </div>

            <!-- Donor Wall Toggle -->
            <div class="flex items-center gap-2 mt-4">
              <input
                id="donor_wall_enabled"
                v-model="newCampaign.donor_wall_enabled"
                type="checkbox"
                class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
              >
              <label for="donor_wall_enabled" class="text-xs font-semibold cursor-pointer">
                Enable Donor Wall
              </label>
              <tooltip
                message="When enabled, donors can choose to have their name displayed publicly on the donor wall for this campaign."
                color="#3a4473"
              />
            </div>

            <!-- Visible Toggle -->
            <div class="flex items-center gap-2 mt-4">
              <input
                id="visible"
                v-model="newCampaign.visible"
                type="checkbox"
                class="w-4 h-4 rounded border-gray-300 text-default_primary_dark focus:ring-default_primary_dark cursor-pointer"
              >
              <label for="visible" class="text-xs font-semibold cursor-pointer">
                Visible
              </label>
              <tooltip
                message="When disabled, this campaign will be hidden from your donation page. You can make it visible later."
                color="#3a4473"
              />
            </div>
          </div>
        </div>
        <button
          class="btn-filled btn-full mt-4"
          @click="save"
        >
          Create
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped lang="scss">
</style>
